import { useState, useEffect, useRef } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { trackNewsletterFunnel } from '@/lib/analytics';
import { api } from '@/lib/api';

interface NewsletterInlineCTAProps {
  source?: string;
}

export function NewsletterInlineCTA({ source = 'feed_card' }: NewsletterInlineCTAProps) {
  const SRC = source;
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const formStartedRef = useRef(false);

  useEffect(() => {
    trackNewsletterFunnel('newsletter_impression', 'general', SRC);
  }, []);

  const handleFormStart = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackNewsletterFunnel('newsletter_form_start', 'general', SRC);
    }
  };

  // ANALYTICS-032: Track field focus
  const handleFocus = () => {
    trackNewsletterFunnel('newsletter_cta_focused', 'general', SRC);
    handleFormStart();
  };

  // ANALYTICS-032: Track blur without submit as abandonment
  const handleBlur = () => {
    if (formStartedRef.current && !submitted && email.length > 0) {
      trackNewsletterFunnel('newsletter_cta_abandoned', 'general', SRC);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      // ANALYTICS-032: Track validation errors
      trackNewsletterFunnel('newsletter_cta_error', 'general', SRC);
      return;
    }
    trackNewsletterFunnel('newsletter_submit', 'general', SRC);

    api.newsletter.subscribe({ email, source: SRC, category: 'general' })
      .then(() => {
        setSubmitted(true);
        trackNewsletterFunnel('newsletter_success', 'general', SRC);
      })
      .catch(() => {
        setError('Something went wrong. Please try again.');
      });
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-600/5 px-5 py-4 flex items-center gap-3">
        <CheckCircle className="h-6 w-6 text-emerald-400 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-text-primary">You're in!</p>
          <p className="text-xs text-text-secondary">Watch your inbox for MotoWalla Weekly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-primary-500/20 bg-gradient-to-br from-primary-500/5 to-primary-600/5 px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500/15 shrink-0">
            <Mail className="h-4 w-4 text-primary-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-text-primary">MotoWalla Weekly</p>
            <p className="text-xs text-text-tertiary">Top 5 stories, every Friday. Free.</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2 sm:w-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="you@example.com"
            className="flex-1 rounded-xl border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-56"
          />
          <button
            type="submit"
            className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Subscribe
          </button>
        </form>
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
